/**
 * Globaler Filter-Store (Zustand).
 *
 * Wird von allen Charts gelesen. Klick auf einen Balken/Tabellenzeile setzt
 * einen Filter, alle Charts reagieren reaktiv.
 *
 * Filter werden außerdem in die URL serialisiert (pushState), damit Dashboards
 * teilbar/bookmarkbar sind.
 */

import { create } from 'zustand';
import type { FilterRequest } from '@statistik/shared-types';

type FilterValue = string[] | { min?: string; max?: string };

interface FilterStore {
  filters: Record<string, FilterValue>;
  /** Anzeige-Labels für Roh-Werte, Key = `${dim}:${value}` (z. B. EAN → "30011 — Kondensmilch"). */
  labels: Record<string, string>;
  addFilter: (dim: string, value: string | number) => void;
  /** Wie addFilter, merkt sich zusätzlich ein menschenlesbares Label für Chips/URL. */
  addFilterWithLabel: (dim: string, value: string | number, label: string) => void;
  removeFilter: (dim: string, value?: string | number) => void;
  setRangeFilter: (dim: string, min?: string, max?: string) => void;
  setAll: (filters: Record<string, FilterValue>) => void;
  clearAll: () => void;
  hasFilter: (dim: string, value?: string | number) => boolean;
  toRequest: () => FilterRequest;
}

export const useFilterStore = create<FilterStore>((set, get) => ({
  filters: {},
  labels: {},
  addFilter: (dim, value) => {
    set((state) => {
      const current = state.filters[dim];
      const arr = Array.isArray(current) ? current : [];
      const sval = String(value);
      if (arr.includes(sval)) return state;
      return { filters: { ...state.filters, [dim]: [...arr, sval] } };
    });
    persistToUrl(get().filters, get().labels);
  },
  addFilterWithLabel: (dim, value, label) => {
    set((state) => {
      const current = state.filters[dim];
      const arr = Array.isArray(current) ? current : [];
      const sval = String(value);
      const labels = { ...state.labels, [`${dim}:${sval}`]: label };
      if (arr.includes(sval)) return { ...state, labels };
      return { filters: { ...state.filters, [dim]: [...arr, sval] }, labels };
    });
    persistToUrl(get().filters, get().labels);
  },
  removeFilter: (dim, value) => {
    set((state) => {
      if (value === undefined) {
        // ganzen Dim-Filter entfernen
        const { [dim]: _, ...rest } = state.filters;
        return { filters: rest };
      }
      const current = state.filters[dim];
      if (!Array.isArray(current)) return state;
      const filtered = current.filter((v) => v !== String(value));
      if (filtered.length === 0) {
        const { [dim]: _, ...rest } = state.filters;
        return { filters: rest };
      }
      return { filters: { ...state.filters, [dim]: filtered } };
    });
    persistToUrl(get().filters, get().labels);
  },
  setRangeFilter: (dim, min, max) => {
    set((state) => {
      if (!min && !max) {
        const { [dim]: _, ...rest } = state.filters;
        return { filters: rest };
      }
      return { filters: { ...state.filters, [dim]: { min, max } } };
    });
    persistToUrl(get().filters, get().labels);
  },
  setAll: (filters) => {
    set({ filters });
    persistToUrl(filters, get().labels);
  },
  clearAll: () => {
    set({ filters: {} });
    persistToUrl({}, get().labels);
  },
  hasFilter: (dim, value) => {
    const current = get().filters[dim];
    if (!current) return false;
    if (value === undefined) return true;
    if (Array.isArray(current)) return current.includes(String(value));
    return false;
  },
  toRequest: () => ({ filters: get().filters }),
}));

function persistToUrl(filters: Record<string, FilterValue>, labels: Record<string, string> = {}) {
  try {
    const url = new URL(window.location.href);
    const sp = url.searchParams;
    sp.delete('f');
    sp.delete('fl');
    if (Object.keys(filters).length > 0) {
      sp.set('f', btoa(encodeURIComponent(JSON.stringify(filters))));
      // Nur Labels mitschreiben, deren Wert noch gefiltert ist (URL kurz halten).
      const active: Record<string, string> = {};
      for (const [key, label] of Object.entries(labels)) {
        const idx = key.indexOf(':');
        const dim = key.slice(0, idx);
        const val = key.slice(idx + 1);
        const cur = filters[dim];
        if (Array.isArray(cur) && cur.includes(val)) active[key] = label;
      }
      if (Object.keys(active).length > 0) {
        sp.set('fl', btoa(encodeURIComponent(JSON.stringify(active))));
      }
    }
    window.history.replaceState(null, '', url.toString());
  } catch {
    /* ignore */
  }
}

/** Laden initialer Filter aus der URL (z.B. via Bookmark). */
export function loadFiltersFromUrl(): void {
  try {
    const sp = new URL(window.location.href).searchParams;
    const f = sp.get('f');
    if (!f) return;
    const parsed = JSON.parse(decodeURIComponent(atob(f))) as Record<string, FilterValue>;
    let labels: Record<string, string> = {};
    const fl = sp.get('fl');
    if (fl) {
      try {
        labels = JSON.parse(decodeURIComponent(atob(fl))) as Record<string, string>;
      } catch {
        /* ignore */
      }
    }
    useFilterStore.setState({ filters: parsed, labels });
  } catch {
    /* ignore */
  }
}
